import { DeleteOutlined, EditOutlined, PlusOutlined, SyncOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { Button, Checkbox, DatePicker, Divider, Form, Image, Input, InputNumber, message, Modal, notification, Select, Space, Table, TableColumnsType, Tag, Tooltip, Tour, ConfigProvider } from 'antd';
import type { TourProps } from 'antd';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import useDrivePicker from 'react-google-drive-picker';
import { coffeeService } from '../services/coffeeService';
import { flavorService } from '../services/flavorService';
import { regionService } from '../services/regionService';
import type { CoffeeBean, RoastLevel } from '../types/coffee';
import type { Flavor } from '../types/flavor';
import type { Region } from '../types/region';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { processingMethodService } from '../services/processingMethodService';
import type { ProcessingMethod } from '../types/processingMethod';
import axios from 'axios';
import { configService } from '../services/configService';
import AdminLayout from '../components/AdminLayout';
import '../styles/AdminPages.css';
import { priceUpdateService } from '../services/priceUpdateService';
import viVN from 'antd/locale/vi_VN';
import CoffeeManagementPreview from '../preview/CoffeeManagement.jpg';
const { Search } = Input;

const { Option } = Select;
const { TextArea } = Input;

type SessionCacheEntry<T> = {
  ts: number;
  data: T;
};

const SESSION_CACHE_PREFIX = '8am_coffee_management_cache_v1';
const CACHE_KEYS = {
  coffees: `${SESSION_CACHE_PREFIX}:coffees`,
  regions: `${SESSION_CACHE_PREFIX}:regions`,
  flavors: `${SESSION_CACHE_PREFIX}:flavors`,
} as const;

const CACHE_TTL_MS = {
  coffees: 60 * 1000, // 1 phút: giảm reads khi quay lại trang liên tục
  regions: 10 * 60 * 1000, // 10 phút: dữ liệu gần như tĩnh
  flavors: 10 * 60 * 1000, // 10 phút: dữ liệu gần như tĩnh
} as const;

let inMemoryCache: {
  coffees?: SessionCacheEntry<CoffeeBean[]>;
  regions?: SessionCacheEntry<Region[]>;
  flavors?: SessionCacheEntry<Flavor[]>;
} = {};

const readSessionCache = <T,>(key: string): SessionCacheEntry<T> | null => {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SessionCacheEntry<T>;
    if (!parsed || typeof parsed.ts !== 'number') return null;
    return parsed;
  } catch {
    return null;
  }
};

const writeSessionCache = <T,>(key: string, entry: SessionCacheEntry<T>) => {
  try {
    sessionStorage.setItem(key, JSON.stringify(entry));
  } catch {
    // ignore (storage quota / private mode)
  }
};

const isCacheFresh = (entry: SessionCacheEntry<unknown>, ttlMs: number) => Date.now() - entry.ts <= ttlMs;

// Thêm component SortableImageItem
const SortableImageItem = ({
  id,
  children,
  onRemove
}: {
  id: string;
  children: React.ReactNode;
  onRemove: () => void;
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id });

  const style = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    transition,
    cursor: 'move',
  };

  return (
    <Tag
      ref={setNodeRef}
      style={style}
      color="blue"
      closable
      onClose={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onRemove();
      }}
      {...attributes}
      {...listeners}
    >
      {children}
    </Tag>
  );
};

// Form cho CuppingScore
const CuppingScoreForm: React.FC<{ isMobile?: boolean }> = ({ isMobile = false }) => (
  <>
    <Divider>Điểm Cupping</Divider>
    <div style={{
      display: 'grid',
      gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(5, 1fr)',
      gap: isMobile ? '8px' : '16px',
      marginBottom: '24px'
    }}>
      <Form.Item name={['cuppingScore', 'fragrance']} label="Fragrance" initialValue={0}>
        <InputNumber min={0} max={10} step={0.25} style={{ width: '100%' }} placeholder="0-10" />
      </Form.Item>
      <Form.Item name={['cuppingScore', 'wetAroma']} label="Wet Aroma" initialValue={0}>
        <InputNumber min={0} max={10} step={0.25} style={{ width: '100%' }} placeholder="0-10" />
      </Form.Item>
      <Form.Item name={['cuppingScore', 'brightness']} label="Brightness" initialValue={0}>
        <InputNumber min={0} max={10} step={0.25} style={{ width: '100%' }} placeholder="0-10" />
      </Form.Item>
      <Form.Item name={['cuppingScore', 'flavor']} label="Flavor" initialValue={0}>
        <InputNumber min={0} max={10} step={0.25} style={{ width: '100%' }} placeholder="0-10" />
      </Form.Item>
      <Form.Item name={['cuppingScore', 'body']} label="Body" initialValue={0}>
        <InputNumber min={0} max={10} step={0.25} style={{ width: '100%' }} placeholder="0-10" />
      </Form.Item>
      <Form.Item name={['cuppingScore', 'finish']} label="Finish" initialValue={0}>
        <InputNumber min={0} max={10} step={0.25} style={{ width: '100%' }} placeholder="0-10" />
      </Form.Item>
      <Form.Item name={['cuppingScore', 'sweetness']} label="Sweetness" initialValue={0}>
        <InputNumber min={0} max={10} step={0.25} style={{ width: '100%' }} placeholder="0-10" />
      </Form.Item>
      <Form.Item name={['cuppingScore', 'cleanCup']} label="Clean Cup" initialValue={0}>
        <InputNumber min={0} max={10} step={0.25} style={{ width: '100%' }} placeholder="0-10" />
      </Form.Item>
      <Form.Item name={['cuppingScore', 'complexity']} label="Complexity" initialValue={0}>
        <InputNumber min={0} max={10} step={0.25} style={{ width: '100%' }} placeholder="0-10" />
      </Form.Item>
      <Form.Item name={['cuppingScore', 'uniformity']} label="Uniformity" initialValue={0}>
        <InputNumber min={0} max={10} step={0.25} style={{ width: '100%' }} placeholder="0-10" />
      </Form.Item>
    </div>
  </>
);

// Form cho FlavorScore
const FlavorScoreForm: React.FC<{ isMobile?: boolean }> = ({ isMobile = false }) => (
  <>
    <Divider>Điểm Hương Vị</Divider>
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(4, 1fr)',
      gap: '16px',
      marginBottom: '24px'
    }}>
      <Form.Item name={['flavorScore', 'floral']} label="Floral" initialValue={0}>
        <InputNumber min={0} max={10} step={0.25} style={{ width: '100%' }} placeholder="0-10" />
      </Form.Item>
      <Form.Item name={['flavorScore', 'honey']} label="Honey" initialValue={0}>
        <InputNumber min={0} max={10} step={0.25} style={{ width: '100%' }} placeholder="0-10" />
      </Form.Item>
      <Form.Item name={['flavorScore', 'sugars']} label="Sugars" initialValue={0}>
        <InputNumber min={0} max={10} step={0.25} style={{ width: '100%' }} placeholder="0-10" />
      </Form.Item>
      <Form.Item name={['flavorScore', 'caramel']} label="Caramel" initialValue={0}>
        <InputNumber min={0} max={10} step={0.25} style={{ width: '100%' }} placeholder="0-10" />
      </Form.Item>
      <Form.Item name={['flavorScore', 'fruits']} label="Fruits" initialValue={0}>
        <InputNumber min={0} max={10} step={0.25} style={{ width: '100%' }} placeholder="0-10" />
      </Form.Item>
      <Form.Item name={['flavorScore', 'citrus']} label="Citrus" initialValue={0}>
        <InputNumber min={0} max={10} step={0.25} style={{ width: '100%' }} placeholder="0-10" />
      </Form.Item>
      <Form.Item name={['flavorScore', 'berry']} label="Berry" initialValue={0}>
        <InputNumber min={0} max={10} step={0.25} style={{ width: '100%' }} placeholder="0-10" />
      </Form.Item>
      <Form.Item name={['flavorScore', 'cocoa']} label="Cocoa" initialValue={0}>
        <InputNumber min={0} max={10} step={0.25} style={{ width: '100%' }} placeholder="0-10" />
      </Form.Item>
      <Form.Item name={['flavorScore', 'nuts']} label="Nuts" initialValue={0}>
        <InputNumber min={0} max={10} step={0.25} style={{ width: '100%' }} placeholder="0-10" />
      </Form.Item>
      <Form.Item name={['flavorScore', 'rustic']} label="Rustic" initialValue={0}>
        <InputNumber min={0} max={10} step={0.25} style={{ width: '100%' }} placeholder="0-10" />
      </Form.Item>
      <Form.Item name={['flavorScore', 'spice']} label="Spice" initialValue={0}>
        <InputNumber min={0} max={10} step={0.25} style={{ width: '100%' }} placeholder="0-10" />
      </Form.Item>
      <Form.Item name={['flavorScore', 'body']} label="Body" initialValue={0}>
        <InputNumber min={0} max={10} step={0.25} style={{ width: '100%' }} placeholder="0-10" />
      </Form.Item>
    </div>
  </>
);

// Form chính
const CoffeeForm: React.FC<{
  onImagesChange: (files: File[]) => void;
  onDriveImageSelect: (fileId: string, webViewLink: string, fileName: string) => void;
  onImagesPreviewChange?: (files: any[]) => void;
  onDriveImagePreviewSelect?: (fileId: string, webViewLink: string, fileName: string) => void;
  selectedFiles: File[];
  selectedFilesPreview?: any[];
  driveImages: { fileId: string; webViewLink: string; fileName: string; }[];
  driveImagesPreview?: { fileId: string; webViewLink: string; fileName: string; }[];
  isMobile?: boolean;
  editingCoffee?: CoffeeBean | null;
  onRemoveLocalImage: (index: number) => void;
  onRemoveLocalPreviewImage: (index: number) => void;
  onRemoveDriveImage: (index: number) => void;
  onRemoveDrivePreviewImage: (index: number) => void;
  setDriveImages: React.Dispatch<React.SetStateAction<{ fileId: string; webViewLink: string; fileName: string; }[]>>;
  setDriveImagesPreview: React.Dispatch<React.SetStateAction<{ fileId: string; webViewLink: string; fileName: string; }[]>>;
  newProcessingMethod: string | null;
  setNewProcessingMethod: React.Dispatch<React.SetStateAction<string | null>>;
  uploadProgress: number;
  // Add refs for modal tour
  nameFieldRef?: React.RefObject<any>;
  codeFieldRef?: React.RefObject<any>;
  roastLevelFieldRef?: React.RefObject<any>;
  beanInfoFieldRef?: React.RefObject<any>;
  regionFieldRef?: React.RefObject<any>;
  flavorFieldRef?: React.RefObject<any>;
  altitudeFieldRef?: React.RefObject<any>;
  processingMethodFieldRef?: React.RefObject<any>;
  brewingMethodsFieldRef?: React.RefObject<any>;
  beanTypeFieldRef?: React.RefObject<any>;
  roastDateFieldRef?: React.RefObject<any>;
  weightPriceFieldRef?: React.RefObject<any>;
  singleOriginFieldRef?: React.RefObject<any>;
  imagesFieldRef?: React.RefObject<any>;
  cuppingScoreFieldRef?: React.RefObject<any>;
  flavorScoreFieldRef?: React.RefObject<any>;
  regionsProp?: Region[];
  regionsLoadingProp?: boolean;
  flavorsProp?: Flavor[];
  flavorsLoadingProp?: boolean;
}> = ({
  onImagesChange,
  onDriveImageSelect,
  onImagesPreviewChange,
  onDriveImagePreviewSelect,
  selectedFiles,
  selectedFilesPreview,
  driveImages,
  driveImagesPreview,
  isMobile = false,
  editingCoffee,
  onRemoveLocalImage,
  onRemoveLocalPreviewImage,
  onRemoveDriveImage,
  onRemoveDrivePreviewImage,
  setDriveImages,
  setDriveImagesPreview,
  newProcessingMethod,
  setNewProcessingMethod,
  uploadProgress,
  // Destructure refs
  nameFieldRef,
  codeFieldRef,
  roastLevelFieldRef,
  beanInfoFieldRef,
  regionFieldRef,
  flavorFieldRef,
  altitudeFieldRef,
  processingMethodFieldRef,
  brewingMethodsFieldRef,
  beanTypeFieldRef,
  roastDateFieldRef,
  weightPriceFieldRef,
  singleOriginFieldRef,
  imagesFieldRef,
  cuppingScoreFieldRef,
  flavorScoreFieldRef,
  regionsProp,
  regionsLoadingProp,
  flavorsProp,
  flavorsLoadingProp,
}) => {
    const [isSingleOrigin, setIsSingleOrigin] = useState(true);
    const [regionsLocal, setRegionsLocal] = useState<Region[]>([]);
    const [regionsLoadingLocal, setRegionsLoadingLocal] = useState(false);
    const [flavorsLocal, setFlavorsLocal] = useState<Flavor[]>([]);
    const [flavorsLoadingLocal, setFlavorsLoadingLocal] = useState(false);
    const regions = regionsProp ?? regionsLocal;
    const regionsLoading = regionsLoadingProp ?? regionsLoadingLocal;
    const flavors = flavorsProp ?? flavorsLocal;
    const flavorsLoading = flavorsLoadingProp ?? flavorsLoadingLocal;
    const [openPicker] = useDrivePicker();
    const [altitudeMin, setAltitudeMin] = useState<number | null>(null);
    const [altitudeMax, setAltitudeMax] = useState<number | null>(null);
    const [processingMethods, setProcessingMethods] = useState<ProcessingMethod[]>([]);
    const [processingMethodsLoading, setProcessingMethodsLoading] = useState(false);
    const regionsFetchedRef = useRef(false);
    const regionsFetchingRef = useRef(false);
    const flavorsFetchedRef = useRef(false);
    const flavorsFetchingRef = useRef(false);
    const processingMethodsFetchedRef = useRef(false);
    const processingMethodsFetchingRef = useRef(false);

    const handleImagesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || []);
      onImagesChange([...selectedFiles, ...files]);
    };

    const handleOpenPicker = () => {
      openPicker({
        clientId: "85231195954-hsetf3cpqpfcr67p2k40001og3054t52.apps.googleusercontent.com",
        developerKey: "AIzaSyD-xnVTuCx7ZDPzxrTmeL4my9FuIfkpYtY",
        viewId: "DOCS",
        showUploadView: true,
        showUploadFolders: true,
        supportDrives: true,
        multiselect: false,
        callbackFunction: (data) => {
          if (data.action === 'picked') {
            const file = data.docs[0];
            onDriveImageSelect(file.id, file.url || '', file.name);
          }
        },
      });
    };

    const fetchRegions = async () => {
      if (regionsProp !== undefined || regionsFetchedRef.current || regionsFetchingRef.current) return;
      regionsFetchingRef.current = true;
      setRegionsLoadingLocal(true);
      try {
        const data = await regionService.getAllRegions();
        setRegionsLocal(data);
        regionsFetchedRef.current = true;
      } catch (error) {
        console.error('Error fetching regions:', error);
      } finally {
        regionsFetchingRef.current = false;
        setRegionsLoadingLocal(false);
      }
    };

    const handleRegionChange = (_values: string[]) => {
      // const lastValue = values[values.length - 1];
      // const existingRegion = regions.find(r => r.name === lastValue);

      // if (!existingRegion) {
      //   try {
      //     await regionService.addRegion({
      //       name: lastValue,
      //       description: `Vùng trồng ${lastValue}`,
      //       country: '',
      //       altitude: {
      //         min: 0,
      //         max: 0
      //       },
      //       climate: '',
      //       soil: '',
      //       createdAt: new Date(),
      //       updatedAt: new Date()
      //     });
      //     // Fetch lại danh sch regions sau khi thêm mới
      //     fetchRegions();
      //   } catch (error) {
      //     console.error('Error adding new region:', error);
      //     message.error('Không thể thêm vùng trồng mới');
      //   }
      // }
    };

    const fetchFlavors = async () => {
      if (flavorsProp !== undefined || flavorsFetchedRef.current || flavorsFetchingRef.current) return;
      flavorsFetchingRef.current = true;
      setFlavorsLoadingLocal(true);
      try {
        const data = await flavorService.getAllFlavors();
        setFlavorsLocal(data);
        flavorsFetchedRef.current = true;
      } catch (error) {
        console.error('Error fetching flavors:', error);
      } finally {
        flavorsFetchingRef.current = false;
        setFlavorsLoadingLocal(false);
      }
    };

    const handleFlavorChange = (_values: string[]) => {
      // const lastValue = values[values.length - 1];
      // const existingFlavor = flavors.find(f => f.name === lastValue);

      // if (!existingFlavor) {
      //   try {
      //     await flavorService.addFlavor({
      //       name: lastValue,
      //       description: `Hương vị ${lastValue}`,
      //       intensity: 5,
      //       createdAt: new Date(),
      //       updatedAt: new Date()
      //     });
      //     fetchFlavors();
      //   } catch (error) {
      //     console.error('Error adding new flavor:', error);
      //     message.error('Không thể thêm hương vị mới');
      //   }
      // }
    };

    const handleAltitudeChange = (value: number | null, type: 'min' | 'max') => {
      if (type === 'min') {
        setAltitudeMin(value);
        // Nếu min > max, tự động cập nhật max
        // if (value && altitudeMax && value > altitudeMax) {
        //   form.setFieldValue(['altitude', 'max'], value);
        //   setAltitudeMax(value);
        // }
      } else {
        setAltitudeMax(value);
        // Nếu max < min, tự động cập nhật min
        // if (value && altitudeMin && value < altitudeMin) {
        //   form.setFieldValue(['altitude', 'min'], value);
        //   setAltitudeMin(value);
        // }
      }
    };

    // Thêm các sensors cho DndContext
    const sensors = useSensors(
      useSensor(PointerSensor),
      useSensor(KeyboardSensor, {
        coordinateGetter: sortableKeyboardCoordinates,
      })
    );

    // Thêm hàm xử lý khi kéo thả kết thúc
    const handleDragEnd = (event: DragEndEvent) => {
      const { active, over } = event;

      if (over && active.id !== over.id) {
        // Xử lý cho local images
        if (active.id.toString().startsWith('local_')) {
          const oldIndex = selectedFiles.findIndex((_, i) => `local_${i}` === active.id);
          const newIndex = selectedFiles.findIndex((_, i) => `local_${i}` === over.id);
          onImagesChange(arrayMove(selectedFiles, oldIndex, newIndex));
          message.success('Thay đổi vị trí ảnh thành công');
        }
        // Xử lý cho local preview images
        else if (active.id.toString().startsWith('preview_') && selectedFilesPreview && onImagesPreviewChange) {
          const oldIndex = selectedFilesPreview.findIndex((_, i) => `preview_${i}` === active.id);
          const newIndex = selectedFilesPreview.findIndex((_, i) => `preview_${i}` === over.id);
          onImagesPreviewChange(arrayMove(selectedFilesPreview, oldIndex, newIndex));
          message.success('Thay đổi vị trí ảnh thành công');
        }
        // Xử lý cho drive images
        else if (active.id.toString().startsWith('drive_')) {
          const oldIndex = driveImages.findIndex((_, i) => `drive_${i}` === active.id);
          const newIndex = driveImages.findIndex((_, i) => `drive_${i}` === over.id);
          setDriveImages(arrayMove(driveImages, oldIndex, newIndex));
          message.success('Thay đổi vị trí ảnh thành công');
        }
        // Xử lý cho drive preview images
        else if (active.id.toString().startsWith('drive_preview_') && driveImagesPreview) {
          const oldIndex = driveImagesPreview.findIndex((_, i) => `drive_preview_${i}` === active.id);
          const newIndex = driveImagesPreview.findIndex((_, i) => `drive_preview_${i}` === over.id);
          setDriveImagesPreview(arrayMove(driveImagesPreview, oldIndex, newIndex));
          message.success('Thay đổi vị trí ảnh thành công');
        }
      }
    };

    const fetchProcessingMethods = async () => {
      if (processingMethodsFetchedRef.current || processingMethodsFetchingRef.current) return;
      processingMethodsFetchingRef.current = true;
      setProcessingMethodsLoading(true);
      try {
        const data = await processingMethodService.getAllProcessingMethods();
        setProcessingMethods(data);
        processingMethodsFetchedRef.current = true;
      } catch (error) {
        console.error('Error fetching processing methods:', error);
      } finally {
        processingMethodsFetchingRef.current = false;
        setProcessingMethodsLoading(false);
      }
    };

    const handleProcessingMethodChange = (values: string[]) => {
      const lastValue = values[values.length - 1];
      const existingMethod = processingMethods.find(m => m.name === lastValue);

      if (!existingMethod) {
        setNewProcessingMethod(lastValue);
      }
    };

    return (
      <div className={`admin-form-container ${isMobile ? 'mobile-coffee-form' : ''}`}>
        <div className="admin-form-section">
          <h3 className="admin-form-section-title">Thông tin cơ bản</h3>
          <div ref={nameFieldRef}>
            <Form.Item name="name" label="Tên" rules={[{ required: true, message: 'Vui lòng nhập tên cà phê' }]}>
              <Input placeholder="Nhập tên cà phê" />
            </Form.Item>
          </div>

          <div ref={codeFieldRef}>
            <Form.Item name="codeCoffee" label="Mã cà phê">
              <Input placeholder="Nhập mã cà phê" />
            </Form.Item>
          </div>

          <div ref={roastLevelFieldRef}>
            <Form.Item name="roastLevel" label="Mức rang" rules={[{ required: true, message: 'Vui lòng chọn mức rang' }]}>
              <Select mode="multiple" placeholder="Chọn mức rang">
                <Option value="light">Light</Option>
                <Option value="medium-light">Medium Light</Option>
                <Option value="medium">Medium</Option>
                <Option value="medium-dark">Medium Dark</Option>
                <Option value="dark">Dark</Option>
              </Select>
            </Form.Item>
          </div>

          <div ref={beanInfoFieldRef}>
            <Form.Item name="beanInfo" label="Thông tin hạt cafe" rules={[{ required: true, message: 'Vui lòng nhập thông tin chi tiết về hạt cà phê' }]}>
              <TextArea rows={4} placeholder="Nhập thông tin chi tiết về hạt cà phê" />
            </Form.Item>
          </div>
        </div>

        <div className="admin-form-section">
          <h3 className="admin-form-section-title">Thông tin vùng trồng và chế biến</h3>
          <div ref={regionFieldRef}>
            <Form.Item name="region" label="Vùng trồng" rules={[{ required: true, message: 'Vui lòng chọn vùng trồng' }]}>
              <Select
                mode="tags"
                loading={regionsLoading}
                onDropdownVisibleChange={(open) => {
                  if (open) fetchRegions();
                }}
                onChange={handleRegionChange}
                placeholder="Chọn hoặc nhập vùng trồng mới"
              >
                {regions.map(region => (
                  <Option key={region.id} value={region.name}>
                    {region.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </div>

          <div ref={flavorFieldRef}>
            <Form.Item name="flavorNotes" label="Hương vị" rules={[{ required: true, message: 'Vui lòng chọn hương vị' }]}>
              <Select
                mode="tags"
                loading={flavorsLoading}
                onDropdownVisibleChange={(open) => {
                  if (open) fetchFlavors();
                }}
                onChange={handleFlavorChange}
                placeholder="Chọn hoặc nhập hương vị mới"
              >
                {flavors.map(flavor => (
                  <Option key={flavor.id} value={flavor.name}>
                    {flavor.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </div>

          <div className="admin-form-row">
            <div ref={altitudeFieldRef}>
              <Form.Item label={<><span style={{ color: '#ff4d4f', marginRight: 4 }}>*</span>Độ cao</>}>
                <Space>
                  <Form.Item
                    name={['altitude', 'min']}
                    noStyle
                    rules={[
                      { required: true, message: 'Vui lòng nhập độ cao tối thiểu' },
                      {
                        validator: async (_, value) => {
                          if (value && altitudeMax && value > altitudeMax) {
                            return Promise.reject('Độ cao tối thiểu phải nhỏ hơn hoặc bằng độ cao tối đa');
                          }
                          return Promise.resolve();
                        }
                      }
                    ]}
                  >
                    <InputNumber
                      min={0}
                      placeholder="Độ cao tối thiểu (m)"
                      style={{ width: 100 }}
                      onChange={(value) => handleAltitudeChange(value, 'min')}
                    />
                  </Form.Item>
                  <span>-</span>
                  <Form.Item
                    name={['altitude', 'max']}
                    noStyle
                    rules={[
                      { required: true, message: 'Vui lòng nhập độ cao tối đa' },
                      {
                        validator: async (_, value) => {
                          if (value && altitudeMin && value < altitudeMin) {
                            return Promise.reject('Độ cao tối đa phải lớn hơn hoặc bằng độ cao tối thiểu');
                          }
                          return Promise.resolve();
                        }
                      }
                    ]}
                  >
                    <InputNumber
                      min={0}
                      placeholder="Độ cao tối đa (m)"
                      style={{ width: 100 }}
                      onChange={(value) => handleAltitudeChange(value, 'max')}
                    />
                  </Form.Item>
                </Space>
              </Form.Item>
            </div>

            <div ref={processingMethodFieldRef}>
              <Form.Item
                name="processingMethod"
                label="Phương pháp sơ chế"
                rules={[{ required: true, message: 'Vui lòng chọn phương pháp sơ chế' }]}
              >
                <Select
                  mode="tags"
                  loading={processingMethodsLoading}
                  onDropdownVisibleChange={(open) => {
                    if (open) fetchProcessingMethods();
                  }}
                  onChange={handleProcessingMethodChange}
                  placeholder="Chọn hoặc nhập phương pháp sơ chế mới"
                >
                  {processingMethods.map(method => (
                    <Option key={method.id} value={method.name}>
                      {method.name === 'natural' ? 'Tự nhiên' :
                        method.name === 'washed' ? 'Ướt' :
                          method.name === 'honey' ? 'Mật ong' : method.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </div>
          </div>
        </div>

        <div className="admin-form-section">
          <h3 className="admin-form-section-title">Phương pháp pha chế và dạng hạt</h3>
          <div className="admin-form-row">
            <div ref={brewingMethodsFieldRef}>
              <Form.Item label="Phương pháp pha chế">
                <Space>
                  <Form.Item name={['brewingMethods', 'espresso']} valuePropName="checked" initialValue={false} noStyle>
                    <Checkbox>Espresso</Checkbox>
                  </Form.Item>
                  <Form.Item name={['brewingMethods', 'pourOver']} valuePropName="checked" initialValue={false} noStyle>
                    <Checkbox>Pour Over</Checkbox>
                  </Form.Item>
                  <Form.Item name={['brewingMethods', 'phin']} valuePropName="checked" initialValue={false} noStyle>
                    <Checkbox>Phin</Checkbox>
                  </Form.Item>
                </Space>
              </Form.Item>
            </div>

            <div ref={beanTypeFieldRef}>
              <Form.Item name="beanType" label="Dạng hạt" rules={[{ required: true, message: 'Vui lòng chọn dạng hạt' }]}>
                <Checkbox.Group>
                  <Space>
                    <Checkbox value="wholeBean">Nguyên hạt</Checkbox>
                    <Checkbox value="grind">Xay sẵn</Checkbox>
                  </Space>
                </Checkbox.Group>
              </Form.Item>
            </div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }} ref={roastDateFieldRef}>
          <Form.Item name="roastDate" label="Ngày rang" rules={[{ required: true, message: 'Vui lòng chọn ngày rang' }]}>
            <DatePicker
              placeholder="Chọn ngày rang"
              style={{ width: '200px' }}
              format="DD/MM/YYYY"
              disabledDate={(current) => {
                return current && current > dayjs().endOf('day');
              }}
            />
          </Form.Item>

          <Form.Item
            name="expirationMonths"
            label="Số tháng hết hạn"
            rules={[{ required: true, message: 'Vui lòng nhập số tháng hết hạn' }]}
          >
            <InputNumber
              min={1}
              placeholder="Nhập số tháng"
              style={{ width: '200px' }}
            />
          </Form.Item>
        </div>

        <Form.Item
          name="purchaseCount"
          label="Số lượng mua"
          tooltip="Số lượng mua ban đầu, mặc định là 0 nếu không nhập"
        >
          <InputNumber
            min={0}
            placeholder="Nhập số lượng mua"
            style={{ width: '200px' }}
          />
        </Form.Item>

        <div ref={weightPriceFieldRef}>
          <Form.Item label={<><span style={{ color: '#ff4d4f', marginRight: 4 }}>*</span> Khối lượng & Giá </>}>
            <Form.List name="weightAndPrice"
              initialValue={[{}]}
              rules={[
                {
                  validator: async (_, value) => {
                    if (!value || value.length < 1) {
                      return Promise.reject(new Error('Vui lòng nhập ít nhất một khối lượng và giá'));
                    }
                  },
                },
              ]}
            >
              {(fields, { add, remove }) => (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    {fields.map(({ key, name }, index) => (
                      <div key={key} style={{ marginBottom: isMobile ? 8 : 16 }}>
                        <Space
                          direction={isMobile ? "vertical" : "horizontal"}
                          align={isMobile ? "start" : "baseline"}
                          style={{ width: '100%', display: 'flex', flexDirection: 'row', alignItems: 'center' }}
                        >
                          <Form.Item
                            name={[name, 'weight']}
                            label={"Khối lượng (g)"}
                            rules={[{ required: true, message: 'Vui lòng nhập khối lượng' }]}
                          >
                            <InputNumber
                              min={0}
                              placeholder="Nhập khối lượng"
                              style={{ width: 150 }}
                            />
                          </Form.Item>

                          <Form.Item
                            name={[name, 'price']}
                            label={"Giá"}
                            rules={[{ required: true, message: 'Vui lòng nhập giá' }]}
                          >
                            <InputNumber
                              min={0}
                              placeholder="Nhập giá"
                              formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                              style={{ width: 150 }}
                            />
                          </Form.Item>

                          {fields.length > 1 && name !== 0 && (
                            <Button danger onClick={() => remove(name)}>
                              Xóa
                            </Button>
                          )}
                        </Space>
                      </div>
                    ))}
                  </div>

                  <Button
                    type="dashed"
                    onClick={() => add()}
                    block
                    style={{ marginTop: 8 }}
                  >
                    + Thêm khối lượng và giá
                  </Button>
                </>
              )}
            </Form.List>
          </Form.Item>
        </div>

        <div ref={singleOriginFieldRef}>
          <Form.Item name="isSingleOrigin" label="Single Origin" valuePropName="checked">
            <Checkbox checked={isSingleOrigin} defaultChecked={true} onChange={e => setIsSingleOrigin(e.target.checked)}>Single Origin</Checkbox>
          </Form.Item>
        </div>

        {!isSingleOrigin && (
          <Form.List name={['blend', 'components']}>
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name }) => (
                  <Space
                    key={key}
                    align="baseline"
                    direction={isMobile ? "vertical" : "horizontal"}
                    style={{ width: '100%' }}
                  >
                    <Form.Item name={[name, 'origin']} rules={[{ required: true, message: 'Vui lòng nhập nguồn gốc blend' }]}>
                      <Input placeholder="Nhập nguồn gốc blend" />
                    </Form.Item>
                    <Form.Item name={[name, 'percentage']} rules={[{ required: true, message: 'Vui lòng nhập tỷ lệ' }]}>
                      <InputNumber min={0} max={100} placeholder="Nhập tỷ lệ %" />
                    </Form.Item>
                    <Button onClick={() => remove(name)} type="link" danger>
                      Xóa
                    </Button>
                  </Space>
                ))}
                <Button type="dashed" onClick={() => add()} block>
                  Thêm thành phần blend
                </Button>
              </>
            )}
          </Form.List>
        )}

        <div ref={imagesFieldRef}>
          <Form.Item
            label={<>Hình ảnh</>}
            required
            rules={[
              {
                validator: async () => {
                  if (selectedFiles.length === 0 && driveImages.length === 0 &&
                    (!editingCoffee || (!editingCoffee.images?.length && !editingCoffee.driveImages?.length))) {
                    throw new Error('Vui lòng chọn ít nhất một hình ảnh');
                  }
                }
              }
            ]}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <Space>
                <Button onClick={() => document.getElementById('fileInput')?.click()}>
                  Chọn ảnh từ local
                </Button>
                <Button onClick={handleOpenPicker}>
                  Chọn ảnh từ Google Drive
                </Button>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImagesChange}
                  id="fileInput"
                  style={{ display: 'none' }}
                />
              </Space>

              {/* Add upload progress indicator */}
              {uploadProgress > 0 && uploadProgress < 100 && (
                <div style={{ marginTop: 8 }}>
                  <div>Đang tải ảnh lên: {uploadProgress}%</div>
                  <div style={{ width: '100%', backgroundColor: '#f0f0f0', borderRadius: 4 }}>
                    <div
                      style={{
                        width: `${uploadProgress}%`,
                        backgroundColor: '#1890ff',
                        height: 8,
                        borderRadius: 4,
                        transition: 'width 0.3s ease'
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Local Files Preview */}
              {selectedFiles.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <div>Ảnh từ local:</div>
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={selectedFiles.map((_, index) => `local_${index}`)}
                      strategy={verticalListSortingStrategy}
                    >
                      <Space wrap>
                        {selectedFiles.map((file, index) => (
                          <SortableImageItem
                            key={`local_${index}`}
                            id={`local_${index}`}
                            onRemove={() => onRemoveLocalImage(index)}
                          >
                            <Space>
                              {'File local'}
                              <Image
                                src={URL.createObjectURL(file)}
                                alt={file.name}
                                style={{ width: 50, height: 50, objectFit: 'cover' }}
                              />
                            </Space>
                          </SortableImageItem>
                        ))}
                      </Space>
                    </SortableContext>
                  </DndContext>
                </div>
              )}

              {/* Drive Images Preview */}
              {driveImages.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <div>Ảnh từ Drive:</div>
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={driveImages.map((_, index) => `drive_${index}`)}
                      strategy={verticalListSortingStrategy}
                    >
                      <Space wrap>
                        {driveImages.map((image, index) => (
                          <SortableImageItem
                            key={`drive_${index}`}
                            id={`drive_${index}`}
                            onRemove={() => onRemoveDriveImage(index)}
                          >
                            <Space>
                              {'Google Drive'}
                              <Image
                                src={`https://lh3.googleusercontent.com/d/${image.fileId}?authuser=server`}
                                alt={image.fileName}
                                style={{ width: 50, height: 50, objectFit: 'cover' }}
                              />
                            </Space>
                          </SortableImageItem>
                        ))}
                      </Space>
                    </SortableContext>
                  </DndContext>
                </div>
              )}

              {/* Existing Images Preview */}
              <div>
                {
                  ((selectedFilesPreview && selectedFilesPreview.length > 0) ||
                    (driveImagesPreview && driveImagesPreview.length > 0)) && (
                    <div>Ảnh trước đó:</div>
                  )
                }

                {selectedFilesPreview && selectedFilesPreview.length > 0 && (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={selectedFilesPreview.map((_, index) => `preview_${index}`)}
                      strategy={verticalListSortingStrategy}
                    >
                      <Space wrap>
                        {selectedFilesPreview.map((file, index) => (
                          <SortableImageItem
                            key={`preview_${index}`}
                            id={`preview_${index}`}
                            onRemove={() => onRemoveLocalPreviewImage(index)}
                          >
                            <Space>
                              {'File local'}
                              <Image
                                src={file}
                                alt={`file-${index}`}
                                style={{ width: 50, height: 50, objectFit: 'cover' }}
                              />
                            </Space>
                          </SortableImageItem>
                        ))}
                      </Space>
                    </SortableContext>
                  </DndContext>
                )}

                {driveImagesPreview && driveImagesPreview.length > 0 && (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={driveImagesPreview.map((_, index) => `drive_preview_${index}`)}
                      strategy={verticalListSortingStrategy}
                    >
                      <Space wrap>
                        {driveImagesPreview.map((image, index) => (
                          <SortableImageItem
                            key={`drive_preview_${index}`}
                            id={`drive_preview_${index}`}
                            onRemove={() => onRemoveDrivePreviewImage(index)}
                          >
                            <Space>
                              {'Google Drive'}
                              <Image
                                src={`https://lh3.googleusercontent.com/d/${image.fileId}?authuser=server`}
                                alt={image.fileName}
                                style={{ width: 50, height: 50, objectFit: 'cover' }}
                              />
                            </Space>
                          </SortableImageItem>
                        ))}
                      </Space>
                    </SortableContext>
                  </DndContext>
                )}
              </div>
            </div>
          </Form.Item>
        </div>

        <div ref={cuppingScoreFieldRef}>
          <CuppingScoreForm isMobile={isMobile} />
        </div>
        <div ref={flavorScoreFieldRef}>
          <FlavorScoreForm isMobile={isMobile} />
        </div>
      </div>
    );
  };

const CoffeeManagement: React.FC = () => {
  const [coffees, setCoffees] = useState<CoffeeBean[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCoffee, setEditingCoffee] = useState<CoffeeBean | null>(null);
  const [form] = Form.useForm();
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [imageFilesPreview, setImageFilesPreview] = useState<any[]>([]);
  const [driveImages, setDriveImages] = useState<{ fileId: string; webViewLink: string; fileName: string; }[]>([]);
  const [driveImagesPreview, setDriveImagesPreview] = useState<{ fileId: string; webViewLink: string; fileName: string; }[]>([]);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const isMobile = windowWidth <= 400;
  const [regions, setRegions] = useState<Region[]>([]);
  const [regionsLoading, setRegionsLoading] = useState(false);
  const [flavors, setFlavors] = useState<Flavor[]>([]);
  const [flavorsLoading, setFlavorsLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [roastLevelFilter, setRoastLevelFilter] = useState<RoastLevel | ''>('');
  const [regionFilter, setRegionFilter] = useState<string>('');
  const [flavorFilter, setFlavorFilter] = useState<string>('');
  const [newProcessingMethod, setNewProcessingMethod] = useState<string | null>(null);

  const [titleClicks, setTitleClicks] = useState(0);
  const [showRefreshButton, setShowRefreshButton] = useState(false);
  const [isCodeModalVisible, setIsCodeModalVisible] = useState(false);
  const [zaloCode, setZaloCode] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [syncLoading, setSyncLoading] = useState(false);
  const [refreshTokenLoading, setRefreshTokenLoading] = useState(false);

  // Thêm refs để theo dõi các timers
  const progressIntervalRef = React.useRef<NodeJS.Timeout | null>(null);
  const progressTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef(true);
  const coffeesFetchedRef = useRef(false);
  const coffeesFetchingRef = useRef(false);
  const regionsFetchedRef = useRef(false);
  const regionsFetchingRef = useRef(false);
  const flavorsFetchedRef = useRef(false);
  const flavorsFetchingRef = useRef(false);

  // Thêm hàm để xóa tất cả các timer khi cần thiết
  const clearAllTimers = useCallback(() => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
    if (progressTimeoutRef.current) {
      clearTimeout(progressTimeoutRef.current);
      progressTimeoutRef.current = null;
    }
  }, []);

  // Xóa timers khi component unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      clearAllTimers();
    };
  }, [clearAllTimers]);

  // Sửa lại useEffect cho window resize
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearAllTimers();
    };
  }, [clearAllTimers]);

  const fetchCoffees = useCallback(async (force = false) => {
    if (coffeesFetchingRef.current) {
      return;
    }
    if (!force && coffeesFetchedRef.current) {
      return;
    }
    if (!force) {
      const mem = inMemoryCache.coffees;
      if (mem && isCacheFresh(mem, CACHE_TTL_MS.coffees)) {
        setCoffees(mem.data);
        coffeesFetchedRef.current = true;
        return;
      }
      const session = readSessionCache<CoffeeBean[]>(CACHE_KEYS.coffees);
      if (session && isCacheFresh(session, CACHE_TTL_MS.coffees)) {
        inMemoryCache.coffees = session;
        setCoffees(session.data);
        coffeesFetchedRef.current = true;
        return;
      }
    }
    coffeesFetchingRef.current = true;
    setLoading(true);
    try {
      const data = await coffeeService.getAllCoffees();
      if (!isMountedRef.current) return;
      setCoffees(data);
      const entry: SessionCacheEntry<CoffeeBean[]> = { ts: Date.now(), data };
      inMemoryCache.coffees = entry;
      writeSessionCache(CACHE_KEYS.coffees, entry);
      coffeesFetchedRef.current = true;
    } catch (error) {
      console.error('Error fetching coffees:', error);
    } finally {
      coffeesFetchingRef.current = false;
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, []);

  const columns: TableColumnsType<CoffeeBean> = [
    {
      title: 'Hình ảnh',
      dataIndex: 'images',
      key: 'images',
      width: 300,
      render: (images: string[], record: CoffeeBean) => (
        <Image.PreviewGroup>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 8,
            maxWidth: 300,
          }}>
            {(record.driveImages && record.driveImages.length > 0) && record.driveImages.map((image, index) => (
              <div key={index} style={{ marginBottom: 8 }}>
                <Image
                  src={`https://lh3.googleusercontent.com/d/${image.fileId}?authuser=server`}
                  alt={image.fileName}
                  style={{ width: 50, height: 50, objectFit: 'cover' }}
                />
              </div>
            ))}

            {images && images.map((url, index) => (
              <div key={index} style={{ marginBottom: 8 }}>
                <Image
                  src={url}
                  alt={`coffee-${index}`}
                  width={50}
                  height={50}
                  style={{ objectFit: 'cover' }}
                />
              </div>
            ))}
          </div>
        </Image.PreviewGroup>
      )
    },
    {
      title: 'Tên',
      dataIndex: 'name',
      key: 'name',
      width: 200,
    },
    {
      title: 'Mã cà phê',
      dataIndex: 'codeCoffee',
      key: 'codeCoffee',
      width: 150,
    },
    {
      title: 'Mức độ rang',
      dataIndex: 'roastLevel',
      key: 'roastLevel',
      width: 200,
      render: (levels: any[]) => (
        <>
          {levels.map((level: any) => {
            let color = 'green';
            switch (level) {
              case 'light':
                color = 'green';
                break;
              case 'medium-light':
                color = 'cyan';
                break;
              case 'medium':
                color = 'blue';
                break;
              case 'medium-dark':
                color = 'orange';
                break;
              case 'dark':
                color = 'red';
                break;
            }
            return (
              <Tag color={color} key={level}>
                {level.split('-').map((word: string) =>
                  word.charAt(0).toUpperCase() + word.slice(1)
                ).join(' ')}
              </Tag>
            );
          })}
        </>
      )
    },
    {
      title: 'Vùng',
      dataIndex: 'region',
      key: 'region',
      width: 200,
      render: (regions: string[]) => regions.map(region => <Tag key={region}>{region}</Tag>)
    },

    {
      title: 'Độ cao',
      dataIndex: 'altitude',
      key: 'altitude',
      width: 200,
      render: (altitude: { min: number; max: number }) =>
        altitude?.min && altitude?.max ? `${altitude.min}m - ${altitude.max}m` : ''
    },
    {
      title: 'Thông tin hạt',
      dataIndex: 'beanInfo',
      key: 'beanInfo',
      width: 200,
      ellipsis: true,
    },
    {
      title: 'Phương pháp sơ chế',
      dataIndex: 'processingMethod',
      key: 'processingMethod',
      width: 200,
      render: (method: string[]) => method.map(method => <Tag key={method}>{method}</Tag>)
    },
    {
      title: 'Hương vị',
      dataIndex: 'flavorNotes',
      key: 'flavorNotes',
      width: 200,
      render: (flavors: string[]) => flavors.map(flavor => <Tag key={flavor}>{flavor}</Tag>)
    },
    {
      title: 'Phương pháp pha',
      dataIndex: 'brewingMethods',
      key: 'brewingMethods',
      width: 200,
      render: (methods: Record<string, boolean>) => {
        const methodNames: Record<string, string> = {
          espresso: 'Espresso',
          pourOver: 'Pour Over',
          phin: 'Phin'
        };
        return Object.entries(methods)
          .filter(([_, enabled]) => enabled)
          .map(([method, _]) => (
            <Tag key={method}>{methodNames[method]}</Tag>
          ));
      }
    },
    {
      title: 'Khối lượng & Giá',
      dataIndex: 'weightAndPrice',
      key: 'weightAndPrice',
      width: 200,
      render: (weightAndPrice: { weight: number; price: number }[]) => (
        <>
          {weightAndPrice?.map((item: any, index: number) => (
            <div key={index}>
              {item.weight}g - {item.price.toLocaleString()}đ
            </div>
          ))}
        </>
      )
    },
    {
      title: 'Ngày rang',
      dataIndex: 'roastDate',
      key: 'roastDate',
      width: 200,
      render: (roastDate: any) => {
        // Kiểm tra nếu roastDate tồn tại
        if (!roastDate) return '';

        // Chuyển đổi Timestamp Firebase sang Date
        // Timestamp Firebase lưu dưới dạng seconds
        const date = new Date(roastDate.seconds * 1000);

        return date.toLocaleDateString('vi-VN', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          // hour: '2-digit',
          // minute: '2-digit'
        });
      }
    },
    {
      title: 'Số tháng hết hạn',
      dataIndex: 'expirationMonths',
      key: 'expirationMonths',
      width: 200,
      render: (months: number) => `${months} tháng`
    },
    {
      title: 'Loại',
      dataIndex: 'isSingleOrigin',
      key: 'isSingleOrigin',
      width: 200,
      render: (isSingleOrigin: boolean) => (
        <Tag color={isSingleOrigin ? 'green' : 'blue'}>
          {isSingleOrigin ? 'Single Origin' : 'Blend'}
        </Tag>
      )
    },
    {
      title: 'Dạng hạt',
      dataIndex: 'beanType',
      key: 'beanType',
      width: 200,
      render: (beanTypes: string[]) => (
        <Space wrap>
          {beanTypes?.includes('wholeBean') && <Tag color="green">Nguyên hạt</Tag>}
          {beanTypes?.includes('grind') && <Tag color="blue">Xay sẵn</Tag>}
        </Space>
      )
    },
    {
      title: 'Số lượng mua',
      dataIndex: 'purchaseCount',
      key: 'purchaseCount',
      width: 150,
      render: (count: number) => count || 0
    },
    {
      title: 'Thao tác',
      key: 'action',
      fixed: 'right',
      width: 200,
      render: (_: any, record: CoffeeBean) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          <Button
            icon={<DeleteOutlined />}
            danger
            onClick={() => handleDelete(record.id!)}
          />
        </Space>
      )
    }
  ];

  const fetchRegions = useCallback(async (force = false) => {
    if (regionsFetchingRef.current) {
      return;
    }
    if (!force && regionsFetchedRef.current) {
      return;
    }
    if (!force) {
      const mem = inMemoryCache.regions;
      if (mem && isCacheFresh(mem, CACHE_TTL_MS.regions)) {
        setRegions(mem.data);
        regionsFetchedRef.current = true;
        return;
      }
      const session = readSessionCache<Region[]>(CACHE_KEYS.regions);
      if (session && isCacheFresh(session, CACHE_TTL_MS.regions)) {
        inMemoryCache.regions = session;
        setRegions(session.data);
        regionsFetchedRef.current = true;
        return;
      }
    }
    regionsFetchingRef.current = true;
    setRegionsLoading(true);
    try {
      const data = await regionService.getAllRegions();
      if (!isMountedRef.current) return;
      setRegions(data);
      const entry: SessionCacheEntry<Region[]> = { ts: Date.now(), data };
      inMemoryCache.regions = entry;
      writeSessionCache(CACHE_KEYS.regions, entry);
      regionsFetchedRef.current = true;
    } catch (error) {
      console.error('Error fetching regions:', error);
    } finally {
      regionsFetchingRef.current = false;
      if (isMountedRef.current) {
        setRegionsLoading(false);
      }
    }
  }, []);

  const fetchFlavors = useCallback(async (force = false) => {
    if (flavorsFetchingRef.current) {
      return;
    }
    if (!force && flavorsFetchedRef.current) {
      return;
    }
    if (!force) {
      const mem = inMemoryCache.flavors;
      if (mem && isCacheFresh(mem, CACHE_TTL_MS.flavors)) {
        setFlavors(mem.data);
        flavorsFetchedRef.current = true;
        return;
      }
      const session = readSessionCache<Flavor[]>(CACHE_KEYS.flavors);
      if (session && isCacheFresh(session, CACHE_TTL_MS.flavors)) {
        inMemoryCache.flavors = session;
        setFlavors(session.data);
        flavorsFetchedRef.current = true;
        return;
      }
    }
    flavorsFetchingRef.current = true;
    setFlavorsLoading(true);
    try {
      const data = await flavorService.getAllFlavors();
      if (!isMountedRef.current) return;
      setFlavors(data);
      const entry: SessionCacheEntry<Flavor[]> = { ts: Date.now(), data };
      inMemoryCache.flavors = entry;
      writeSessionCache(CACHE_KEYS.flavors, entry);
      flavorsFetchedRef.current = true;
    } catch (error) {
      console.error('Error fetching flavors:', error);
    } finally {
      flavorsFetchingRef.current = false;
      if (isMountedRef.current) {
        setFlavorsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    fetchCoffees();
    fetchRegions();
    fetchFlavors();
  }, [fetchCoffees, fetchRegions, fetchFlavors]);

  const handleEdit = (coffee: CoffeeBean) => {
    // Xóa tất cả timers trước
    clearAllTimers();

    // Reset trạng thái loading trước
    setSubmitLoading(false);
    setUploadProgress(0);

    // Thiết lập dữ liệu cho editor
    setEditingCoffee(coffee);
    setImageFiles([]);
    setDriveImages([]);

    if (coffee.driveImages) {
      setDriveImagesPreview(coffee.driveImages.map(img => ({
        ...img,
        fileName: img.fileName || 'Untitled'
      })));
    } else {
      setDriveImagesPreview([]);
    }

    if (coffee.images) {
      setImageFilesPreview(coffee.images);
    } else {
      setImageFilesPreview([]);
    }

    const formData = {
      ...coffee,
      roastDate: coffee.roastDate?.seconds ?
        dayjs(new Date(coffee.roastDate.seconds * 1000)) :
        dayjs(coffee.roastDate)
    };

    form.setFieldsValue(formData);

    // Đảm bảo không có trạng thái loading nào khi mở modal
    progressTimeoutRef.current = setTimeout(() => {
      setSubmitLoading(false);
      setUploadProgress(0);
      progressTimeoutRef.current = null;
    }, 100);

    setModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    Modal.confirm({
      title: 'Xác nhận xóa',
      content: 'Bạn có chắc chắn muốn xóa cà phê này không?',
      okText: 'Xóa',
      okType: 'danger',
      cancelText: 'Hủy',
      onOk: async () => {
        try {
          await coffeeService.deleteCoffee(id);
          // Tránh đọc lại toàn bộ danh sách sau khi xóa (giảm reads/cost)
          setCoffees(prev => {
            const next = prev.filter(c => c.id !== id);
            const entry: SessionCacheEntry<CoffeeBean[]> = { ts: Date.now(), data: next };
            inMemoryCache.coffees = entry;
            writeSessionCache(CACHE_KEYS.coffees, entry);
            return next;
          });
          coffeesFetchedRef.current = true;
          message.success('Xóa cà phê thành công');

          // Gọi api gửi dữ liệu cà phê để AI tự động cập nhật
          void axios.get(`https://coffee-ai.8am.vn/update`, {
            httpsAgent: false,
            proxy: false
          }).catch((aiError) => {
            console.error('Error updating AI data:', aiError);
          });
        } catch (error) {
          console.error('Error deleting coffee:', error);
          message.error('Không thể xóa cà phê');
        }
      },
    });
  };

  const handleImagesChange = (files: File[]) => {
    setImageFiles(files);
  };

  const handleDriveImageSelect = (fileId: string, webViewLink: string, fileName: string) => {
    setDriveImages(prev => [...prev, { fileId, webViewLink, fileName }]);
  };

  const handleRemoveLocalImage = (index: number) => {
    setImageFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleRemoveLocalPreviewImage = (index: number) => {
    setImageFilesPreview(prev => prev.filter((_, i) => i !== index));
  };

  const handleRemoveDriveImage = (index: number) => {
    setDriveImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleRemoveDrivePreviewImage = (index: number) => {
    setDriveImagesPreview(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (values: any) => {
    try {
      // Xóa các timer cũ trước khi bắt đầu
      clearAllTimers();

      setSubmitLoading(true);
      setUploadProgress(0);

      // Nếu có phương pháp sơ chế mới, lưu vào DB trước
      if (newProcessingMethod) {
        await saveNewProcessingMethod();
      }

      // Kiểm tra hình ảnh
      if (imageFiles.length === 0 && driveImages.length === 0 &&
        (!editingCoffee || (!editingCoffee.images?.length && !editingCoffee.driveImages?.length))) {
        message.error('Vui lòng chọn ít nhất một hình ảnh');
        setSubmitLoading(false);
        return;
      }

      const lstDriveImages: any[] = [];
      const newsLstImage: any[] = [];
      const oldLstImage: any[] = [];

      imageFiles.forEach(file => {
        newsLstImage.push(file);
      });

      imageFilesPreview?.forEach(file => {
        oldLstImage.push(file);
      });

      driveImages.forEach(image => {
        lstDriveImages.push({
          fileId: image.fileId,
          webViewLink: image.webViewLink,
          fileName: image.fileName
        });
      });

      driveImagesPreview?.forEach(image => {
        lstDriveImages.push({
          fileId: image.fileId,
          webViewLink: image.webViewLink,
          fileName: image.fileName
        });
      });

      const formattedValues = {
        ...values,
        // Chuyển đổi roastDate từ dayjs sang Date
        roastDate: values.roastDate ? values.roastDate.toDate() : new Date(),
        driveImages: lstDriveImages,
        // Đảm bảo purchaseCount luôn có giá trị, mặc định là 0
        purchaseCount: values.purchaseCount || 0,
        // Đảm bảo isSingleOrigin luôn có giá trị, mặc định là true
        isSingleOrigin: typeof values.isSingleOrigin === 'boolean' ? values.isSingleOrigin : true,
      };

      // Hiển thị progress indicator cho quá trình tải ảnh
      if (newsLstImage.length > 0) {
        setUploadProgress(10); // Bắt đầu ở 10%

        // Simulate progress updates và lưu reference để có thể clear
        progressIntervalRef.current = setInterval(() => {
          setUploadProgress(prev => {
            if (prev >= 90) {
              if (progressIntervalRef.current) {
                clearInterval(progressIntervalRef.current);
                progressIntervalRef.current = null;
              }
              return 90;
            }
            return prev + 5; // Tăng 5% mỗi lần
          });
        }, 1000);

        // Clear interval khi component unmount và lưu reference để có thể clear
        progressTimeoutRef.current = setTimeout(() => {
          if (progressIntervalRef.current) {
            clearInterval(progressIntervalRef.current);
            progressIntervalRef.current = null;
          }
        }, 30000); // Tối đa 30s
      }

      try {
        if (editingCoffee) {
          await coffeeService.updateCoffee(
            editingCoffee.id!,
            formattedValues,
            newsLstImage,
            oldLstImage
          );
          message.success('Cập nhật cà phê thành công');
        } else {
          await coffeeService.addCoffee(formattedValues, imageFiles);
          message.success('Thêm cà phê mới thành công');
        }

        setUploadProgress(100);

        setModalVisible(false);
        form.resetFields();
        setImageFiles([]);
        setImageFilesPreview([]);
        setDriveImages([]);
        setDriveImagesPreview([]);
        fetchCoffees(true);

        // Gọi api gửi dữ liệu cà phê để AI tự động cập nhật
        // try {
        //   const response = await axios.get(`https://coffee-ai.8am.vn/update`);
        //   console.log(response);
        // } catch (aiError) {
        //   // Không cần phải hiển thị lỗi này cho người dùng vì đã lưu cà phê thành công
        //   console.error('Error updating AI data:', aiError);
        // }
      } catch (error: any) {
        // Hiển thị thông báo lỗi cụ thể hơn
        if (error.message?.includes('timeout') || error.message?.includes('504')) {
          notification.error({
            message: 'Lỗi kết nối',
            description: 'Máy chủ mất quá nhiều thời gian để phản hồi. Dữ liệu có thể đã được lưu, vui lòng kiểm tra danh sách cà phê.',
            duration: 10
          });
        } else {
          message.error(error.message || 'Có lỗi xảy ra khi lưu thông tin');
        }
      }
    } catch (error: any) {
      console.error('Error saving coffee:', error);
      message.error(error.message || 'Có lỗi xảy ra khi lưu thông tin');
    } finally {
      setSubmitLoading(false);
      // Đảm bảo reset progress về 0 nếu có lỗi nhưng giữ lại reference để có thể clear
      progressTimeoutRef.current = setTimeout(() => {
        setUploadProgress(0);
        progressTimeoutRef.current = null;
      }, 1000);
    }
  };

  const getMobileColumns = (): TableColumnsType<any> => [
    {
      title: 'Thông tin',
      dataIndex: 'name',
      key: 'info',
      render: (_, record: CoffeeBean) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
            {record.driveImages && record.driveImages.length > 0 ? (
              <Image
                src={`https://lh3.googleusercontent.com/d/${record.driveImages[0].fileId}?authuser=server`}
                alt="coffee"
                style={{ width: 40, height: 40, objectFit: 'cover' }}
              />
            ) : record.images && record.images.length > 0 ? (
              <Image
                src={record.images[0]}
                alt="coffee"
                style={{ width: 40, height: 40, objectFit: 'cover' }}
              />
            ) : null}
            <div style={{ flex: 1 }}>
              <strong>{record.name}</strong>
              {record.codeCoffee && (
                <div style={{ fontSize: '12px', color: '#888' }}>
                  Mã: {record.codeCoffee}
                </div>
              )}
              <div style={{
                marginTop: 4,
                display: '-webkit-box',
                WebkitLineClamp: 6,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                fontSize: '12px',
                color: '#666'
              }}>
                {record.beanInfo}
              </div>
            </div>
          </div>

          <div style={{ fontSize: '12px' }}>
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              {/* Mức rang */}
              <div>
                <strong>Mức rang:</strong>{' '}
                {record.roastLevel?.map(level => (
                  <Tag key={level} color="blue" style={{ fontSize: '11px' }}>
                    {level}
                  </Tag>
                ))}
              </div>

              {/* Vùng trồng */}
              <div>
                <strong>Vùng trồng:</strong>{' '}
                {record.region?.map(r => (
                  <Tag key={r} style={{ fontSize: '11px' }}>{r}</Tag>
                ))}
              </div>

              {/* Phương pháp sơ chế */}
              <div>
                <strong>Sơ chế:</strong>{' '}
                <Tag style={{ fontSize: '11px' }}>{record.processingMethod}</Tag>
              </div>

              {/* Dạng hạt */}
              <div>
                <strong>Dạng hạt:</strong>{' '}
                {record.beanType?.includes('wholeBean') && <Tag color="green" style={{ fontSize: '11px' }}>Nguyên hạt</Tag>}
                {record.beanType?.includes('grind') && <Tag color="blue" style={{ fontSize: '11px' }}>Xay sẵn</Tag>}
              </div>

              {/* Số lượng mua */}
              <div>
                <strong>Số lượng mua:</strong>{' '}
                <Tag color="purple" style={{ fontSize: '11px' }}>{record.purchaseCount || 0}</Tag>
              </div>

              {/* Giá và khối lượng */}
              <div>
                <strong>Giá:</strong>{' '}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                  {record.weightAndPrice?.map((item, index) => (
                    <Tag key={index} color="orange" style={{ fontSize: '11px' }}>
                      {item.weight}g - {item.price.toLocaleString()}đ
                    </Tag>
                  ))}
                </div>
              </div>
            </Space>
          </div>
        </div>
      )
    },
    {
      title: 'Thao tác',
      key: 'action',
      render: (_: any, record: CoffeeBean) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          <Button
            icon={<DeleteOutlined />}
            danger
            onClick={() => handleDelete(record.id!)}
          />
        </Space>
      )
    }
  ];

  const filteredCoffees = useMemo(() => {
    return coffees.filter((coffee) => {
      const matchesSearch = coffee.name.toLowerCase().includes(searchText.toLowerCase());
      const matchesRoastLevel = roastLevelFilter ? coffee.roastLevel.includes(roastLevelFilter) : true;
      const matchesRegion = regionFilter ? coffee.region.includes(regionFilter) : true;
      const matchesFlavor = flavorFilter ? coffee.flavorNotes.includes(flavorFilter) : true;
      return matchesSearch && matchesRoastLevel && matchesRegion && matchesFlavor;
    });
  }, [coffees, searchText, roastLevelFilter, regionFilter, flavorFilter]);

  // Trong CoffeeManagement component, thêm handlers cho việc cập nhật thứ tự ảnh
  const handleImagesPreviewChange = (newFiles: any[]) => {
    setImageFilesPreview(newFiles);
  };

  const handleCloseModal = () => {
    clearAllTimers();
    setModalVisible(false);
    setIsModalTourOpen(false); // Reset modal tour state
    setImageFiles([]);
    setDriveImages([]);
    setImageFilesPreview([]);
    setDriveImagesPreview([]);
    setNewProcessingMethod(null);
    setSubmitLoading(false);
    setUploadProgress(0);
    form.resetFields();
  };

  const saveNewProcessingMethod = async () => {
    if (newProcessingMethod) {
      try {
        await processingMethodService.addProcessingMethod({
          name: newProcessingMethod,
          description: `Phương pháp sơ chế ${newProcessingMethod}`,
          createdAt: new Date(),
          updatedAt: new Date()
        });
        setNewProcessingMethod(null);

        // Gọi api gửi dữ liệu phương pháp sơ chế để AI tự động cập nhật
        const response = await axios.get(`https://coffee-ai.8am.vn/update`, {
          httpsAgent: false,
          proxy: false
        });
        console.log(response);
      } catch (error) {
        console.error('Error adding new processing method:', error);
        message.error('Không thể thêm phương pháp sơ chế mới');
      }
    }
  };

  const handleTitleClick = () => {
    const newCount = titleClicks + 1;
    setTitleClicks(newCount);
    if (newCount >= 10) {
      setShowRefreshButton(true);
    }
  };

  const handleRefreshToken = async () => {
    if (refreshTokenLoading) return;
    setRefreshTokenLoading(true);
    try {
      const response = await axios.post(`https://oauth.zaloapp.com/v4/oa/access_token`, {
        app_id: '2448144731783137375',
        grant_type: 'authorization_code',
        code: zaloCode
      }, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'secret_key': 'g8RUo6XKj3V7RoSuEom1'
        }
      });

      if (response.data) {
        await configService.saveZaloTokens(
          response.data.access_token,
          response.data.refresh_token,
          response.data.expires_in
        );
        notification.success({
          message: 'Thành công',
          description: 'Đã cập nhật token thành công',
          duration: 2
        });
        setIsCodeModalVisible(false);
        setZaloCode('');
      }
    } catch (error) {
      console.error('error', error);
      notification.error({
        message: 'Lỗi',
        description: 'Không thể cập nhật token. Vui lòng thử lại sau.',
        duration: 2
      });
    } finally {
      setRefreshTokenLoading(false);
    }
  };

  const showCodeModal = () => {
    setIsCodeModalVisible(true);
  };

  const handleCodeModalCancel = () => {
    setIsCodeModalVisible(false);
    setZaloCode('');
  };

  // Thêm function để cập nhật giá từ customization
  const updatePricesFromCustomization = async () => {
    if (syncLoading) return;
    setSyncLoading(true);
    try {
      const result = await priceUpdateService.updateCoffeePricesFromCustomization();

      if (result.errors.length > 0) {
        Modal.warning({
          title: 'Cập nhật giá',
          content: (
            <div>
              <p>Đã cập nhật {result.updated} cà phê, không thay đổi {result.noChanges} cà phê.</p>
              <p>Có {result.errors.length} lỗi xảy ra:</p>
              <ul style={{ maxHeight: '200px', overflowY: 'auto' }}>
                {result.errors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          ),
          width: 600
        });
      } else {
        notification.success({
          message: 'Cập nhật giá thành công',
          description: `Đã cập nhật ${result.updated} cà phê, không thay đổi ${result.noChanges} cà phê.`,
          duration: 5
        });
      }

      // Tải lại danh sách cà phê để hiển thị giá mới
      await fetchCoffees(true);
    } catch (error) {
      console.error('Error syncing prices:', error);
      notification.error({
        message: 'Lỗi cập nhật giá',
        description: 'Đã xảy ra lỗi khi cập nhật giá cà phê từ dữ liệu customization.',
        duration: 5
      });
    } finally {
      setSyncLoading(false);
    }
  };

  const [isTourOpen, setIsTourOpen] = useState(false);
  const [isModalTourOpen, setIsModalTourOpen] = useState(false);

  // References for tour targets
  const titleRef = useRef(null);
  const searchRef = useRef(null);
  const filtersRef = useRef(null);
  const addButtonRef = useRef(null);
  const syncButtonRef = useRef(null);
  const tableRef = useRef(null);
  const tableContainerRef = useRef(null);

  // References for modal tour targets
  const modalTitleRef = useRef(null);
  const nameFieldRef = useRef(null);
  const codeFieldRef = useRef(null);
  const roastLevelFieldRef = useRef(null);
  const beanInfoFieldRef = useRef(null);
  const regionFieldRef = useRef(null);
  const flavorFieldRef = useRef(null);
  const altitudeFieldRef = useRef(null);
  const processingMethodFieldRef = useRef(null);
  const brewingMethodsFieldRef = useRef(null);
  const beanTypeFieldRef = useRef(null);
  const roastDateFieldRef = useRef(null);
  const weightPriceFieldRef = useRef(null);
  const singleOriginFieldRef = useRef(null);
  const imagesFieldRef = useRef(null);
  const cuppingScoreFieldRef = useRef(null);
  const flavorScoreFieldRef = useRef(null);
  const submitButtonRef = useRef(null);

  const tourSteps: TourProps['steps'] = [
    {
      title: 'Quản lý hạt cà phê',
      description: 'Chào mừng bạn đến với trang Quản lý hạt cà phê! Đây là nơi bạn có thể quản lý danh sách các loại hạt cà phê của cửa hàng, thêm mới, chỉnh sửa và cập nhật thông tin chi tiết về các loại cà phê.',
      target: () => titleRef.current,
    },
    {
      title: 'Tìm kiếm',
      description: 'Tìm kiếm nhanh các loại hạt cà phê theo tên. Nhập từ khóa và kết quả sẽ được lọc ngay lập tức.',
      target: () => searchRef.current,
    },
    {
      title: 'Bộ lọc',
      description: 'Lọc danh sách cà phê theo các tiêu chí như mức rang, vùng trồng và hương vị. Các bộ lọc có thể kết hợp với nhau để tìm chính xác loại hạt cà phê bạn cần.',
      target: () => filtersRef.current,
    },
    {
      title: 'Thêm cà phê mới',
      description: 'Nhấn vào đây để thêm một loại hạt cà phê mới vào hệ thống. Bạn sẽ cần điền các thông tin chi tiết như tên, mức rang, vùng trồng, hương vị, giá, v.v.',
      target: () => addButtonRef.current,
    },
    {
      title: 'Đồng bộ giá',
      description: 'Cập nhật giá hạt cà phê từ dữ liệu của ipos. Chức năng này giúp đồng bộ giá hạt cà phê với các kênh bán hàng khác.',
      target: () => syncButtonRef.current,
    },
    {
      title: 'Bảng dữ liệu cà phê',
      description: 'Bảng hiển thị tất cả các loại hạt cà phê đã được thêm vào hệ thống. Mỗi hàng chứa thông tin chi tiết về một loại hạt cà phê bao gồm hình ảnh, tên, mã, mức rang, vùng trồng, hương vị và các nút thao tác để chỉnh sửa hoặc xóa.',
      target: () => tableContainerRef.current,
    },
    {
      title: 'Kết thúc hướng dẫn!',
      description: 'Cảm ơn bạn đã xem hướng dẫn này. Bây giờ bạn đã sẵn sàng quản lý danh mục hạt cà phê của cửa hàng. Bạn có thể thêm mới, chỉnh sửa, xóa các loại hạt cà phê và sử dụng các bộ lọc để tìm kiếm nhanh chóng. Nếu cần xem lại hướng dẫn, hãy nhấn vào nút "Hướng dẫn" ở góc trên.',
      target: () => titleRef.current,
    },
  ];

  const modalTourSteps: TourProps['steps'] = [
    {
      title: 'Form thêm hạt cà phê',
      description: 'Đây là form để thêm thông tin hạt cà phê mới. Hãy điền đầy đủ các thông tin bắt buộc để tạo sản phẩm hoàn chỉnh.',
      target: () => modalTitleRef.current,
    },
    {
      title: 'Tên hạt cà phê',
      description: 'Nhập tên của loại hạt cà phê (VD: Arabica Cầu Đất, Robusta Đắk Lắk). Đây là thông tin bắt buộc và sẽ hiển thị cho khách hàng.',
      target: () => nameFieldRef.current,
    },
    {
      title: 'Mã cà phê',
      description: 'Nhập mã định danh cho hạt cà phê (VD: CB001, AR002). Mã này giúp quản lý và phân biệt các sản phẩm.',
      target: () => codeFieldRef.current,
    },
    {
      title: 'Mức rang',
      description: 'Chọn mức độ rang của hạt cà phê. Có thể chọn nhiều mức: Light (rang nhẹ), Medium (rang vừa), Dark (rang đậm), v.v. Đây là thông tin bắt buộc.',
      target: () => roastLevelFieldRef.current,
    },
    {
      title: 'Thông tin hạt cà phê',
      description: 'Mô tả chi tiết về đặc điểm của hạt cà phê như nguồn gốc, đặc tính hương vị, cách thức canh tác, v.v. Thông tin này giúp khách hàng hiểu rõ hơn về sản phẩm.',
      target: () => beanInfoFieldRef.current,
    },
    {
      title: 'Vùng trồng',
      description: 'Chọn hoặc nhập vùng trồng cà phê (VD: Cầu Đất, Đắk Lắk, Guatemala). Có thể chọn nhiều vùng cho blend coffee. Đây là thông tin bắt buộc.',
      target: () => regionFieldRef.current,
    },
    {
      title: 'Hương vị',
      description: 'Chọn hoặc nhập các note hương vị đặc trưng (VD: Chocolate, Caramel, Citrus, Floral). Giúp khách hàng dễ dàng lựa chọn theo sở thích.',
      target: () => flavorFieldRef.current,
    },
    {
      title: 'Độ cao trồng',
      description: 'Nhập độ cao nơi trồng cà phê tính bằng mét. Độ cao ảnh hưởng đến chất lượng và hương vị của hạt cà phê. Cần nhập cả độ cao tối thiểu và tối đa.',
      target: () => altitudeFieldRef.current,
    },
    {
      title: 'Phương pháp sơ chế',
      description: 'Chọn phương pháp sơ chế hạt cà phê như Washed (ướt), Natural (khô), Honey (mật ong). Phương pháp này ảnh hưởng đến hương vị cuối cùng.',
      target: () => processingMethodFieldRef.current,
    },
    {
      title: 'Phương pháp pha chế',
      description: 'Chọn các phương pháp pha phù hợp với loại hạt này: Espresso, Pour Over (drip), Phin. Giúp khách hàng biết cách pha tối ưu.',
      target: () => brewingMethodsFieldRef.current,
    },
    {
      title: 'Dạng hạt',
      description: 'Chọn dạng bán của hạt cà phê: Nguyên hạt (whole bean) hoặc Xay sẵn (ground). Có thể chọn cả hai dạng.',
      target: () => beanTypeFieldRef.current,
    },
    {
      title: 'Ngày rang và hạn sử dụng',
      description: 'Chọn ngày rang và nhập số tháng hết hạn. Thông tin này quan trọng để đảm bảo chất lượng và độ tươi của cà phê.',
      target: () => roastDateFieldRef.current,
    },
    {
      title: 'Khối lượng và giá',
      description: 'Thêm các gói khối lượng khác nhau và giá tương ứng (VD: 250g - 120,000đ, 500g - 220,000đ). Ít nhất phải có một gói giá. Đây là thông tin bắt buộc.',
      target: () => weightPriceFieldRef.current,
    },
    {
      title: 'Single Origin',
      description: 'Đánh dấu nếu đây là cà phê Single Origin (từ một vùng duy nhất) hoặc bỏ đánh dấu nếu là Blend (pha trộn từ nhiều vùng).',
      target: () => singleOriginFieldRef.current,
    },
    {
      title: 'Hình ảnh sản phẩm',
      description: 'Thêm hình ảnh cho sản phẩm từ máy tính hoặc Google Drive. Ít nhất cần một hình ảnh. Có thể kéo thả để sắp xếp thứ tự hiển thị.',
      target: () => imagesFieldRef.current,
    },
    {
      title: 'Điểm Cupping',
      description: 'Đánh giá chất lượng theo tiêu chuẩn cupping với các tiêu chí như Fragrance, Flavor, Body, v.v. Điểm từ 0-10 cho mỗi tiêu chí.',
      target: () => cuppingScoreFieldRef.current,
    },
    {
      title: 'Điểm Hương vị',
      description: 'Đánh giá chi tiết các note hương vị như Floral, Honey, Fruits, Chocolate, v.v. Giúp mô tả profile hương vị một cách chính xác.',
      target: () => flavorScoreFieldRef.current,
    },
    {
      title: 'Hoàn thành',
      description: (
        <div>
          <p>Kiểm tra lại tất cả thông tin và nhấn "Thêm mới" để lưu hạt cà phê vào hệ thống. Đảm bảo đã điền đầy đủ các thông tin bắt buộc.</p>
          <div style={{ marginTop: 16, textAlign: 'center' }}>
            <p style={{ fontWeight: 'bold', marginBottom: 8 }}>Preview: Hạt cà phê sau khi thêm thành công sẽ hiển thị như này:</p>
            <Image
              src={CoffeeManagementPreview}
              alt="Preview hạt cà phê sau khi thêm"
              style={{
                maxWidth: '100%',
                height: 'auto',
                maxHeight: '300px',
                border: '1px solid #d9d9d9',
                borderRadius: '6px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                cursor: 'pointer'
              }}
              preview={{
                mask: 'Click để phóng to'
              }}
            />
          </div>
        </div>
      ),
      target: () => submitButtonRef.current,
    },
  ];

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <div className="admin-page-title">
          <h1 onClick={handleTitleClick} ref={titleRef}>Quản lý hạt cà phê</h1>
          <Tag color="blue">{coffees.length} cà phê</Tag>
          {showRefreshButton && (
            <Button type="primary" onClick={showCodeModal}>
              Refresh Token
            </Button>
          )}
          <Button
            type="primary"
            icon={<QuestionCircleOutlined />}
            onClick={() => setIsTourOpen(true)}
            style={{ marginLeft: 8 }}
          >
            Hướng dẫn
          </Button>
        </div>

        <div className="admin-page-actions">
          <div className="admin-search-filters" ref={filtersRef}>
            <div ref={searchRef}>
              <Search
                placeholder="Tìm theo tên cà phê"
                allowClear
                style={{ width: isMobile ? '100%' : 300 }}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </div>
            <Select
              placeholder="Lọc theo mức rang"
              style={{ width: isMobile ? '100%' : 200 }}
              allowClear
              onChange={(value) => setRoastLevelFilter(value)}
              showSearch
              filterOption={(input, option) =>
                (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
              }
            >
              <Select.Option value="light">Light</Select.Option>
              <Select.Option value="medium-light">Medium Light</Select.Option>
              <Select.Option value="medium">Medium</Select.Option>
              <Select.Option value="medium-dark">Medium Dark</Select.Option>
              <Select.Option value="dark">Dark</Select.Option>
            </Select>
            <Select
              placeholder="Lọc theo vùng"
              style={{ width: isMobile ? '100%' : 200 }}
              allowClear
              onChange={(value) => setRegionFilter(value)}
              showSearch
              filterOption={(input, option) =>
                (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
              }
            >
              {regions.map(region => (
                <Select.Option key={region.id} value={region.name}>
                  {region.name}
                </Select.Option>
              ))}
            </Select>
            <Select
              placeholder="Lọc theo hương vị"
              style={{ width: isMobile ? '100%' : 200 }}
              allowClear
              onChange={(value) => setFlavorFilter(value)}
              showSearch
              filterOption={(input, option) =>
                (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
              }
            >
              {flavors.map(flavor => (
                <Select.Option key={flavor.id} value={flavor.name}>
                  {flavor.name}
                </Select.Option>
              ))}
            </Select>
          </div>

          <Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                clearAllTimers();
                setEditingCoffee(null);
                setSubmitLoading(false);
                setUploadProgress(0);
                setImageFiles([]);
                setDriveImages([]);
                setImageFilesPreview([]);
                setDriveImagesPreview([]);
                form.resetFields();
                setModalVisible(true);
              }}
              className="admin-button"
              ref={addButtonRef}
            >
              Thêm cà phê mới
            </Button>

            <Tooltip title="Cập nhật giá cà phê từ dữ liệu customization">
              <Button
                type="primary"
                icon={<SyncOutlined spin={syncLoading} />}
                onClick={updatePricesFromCustomization}
                loading={syncLoading}
                className="admin-button"
                ref={syncButtonRef}
              >
                Đồng bộ giá
              </Button>
            </Tooltip>
          </Space>
        </div>
      </div>

      <div className="admin-table-container" ref={tableContainerRef}>
        <Table
          className="admin-table"
          columns={isMobile ? getMobileColumns() : columns}
          dataSource={filteredCoffees}
          loading={loading}
          rowKey="id"
          scroll={{ x: isMobile ? undefined : 2500 }}
          pagination={{
            responsive: true,
            defaultPageSize: isMobile ? 5 : 10,
            position: isMobile ? ['bottomCenter'] : ['bottomRight']
          }}
          ref={tableRef}
        />
      </div>

      {/* Add Tour component */}
      <ConfigProvider locale={viVN}>
        <Tour
          open={isTourOpen}
          onClose={() => setIsTourOpen(false)}
          steps={tourSteps}
          indicatorsRender={(current, total) => (
            <span>{current + 1} / {total}</span>
          )}
          type="primary"
          arrow={true}
          placement="bottom"
          mask={{
            style: {
              boxShadow: 'inset 0 0 15px #333',
            },
          }}
        />
      </ConfigProvider>

      <Modal
        title={editingCoffee ? "Sửa thông tin cà phê" : "Thêm cà phê mới"}
        visible={modalVisible}
        onCancel={handleCloseModal}
        footer={null}
        width={800}
        className="admin-modal"
        destroyOnClose={true}
        afterClose={() => {
          clearAllTimers();
          setSubmitLoading(false);
          setUploadProgress(0);
        }}
      >
        <div className="admin-form-container">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 ref={modalTitleRef} style={{ margin: 0 }}>
              {editingCoffee ? "Cập nhật thông tin hạt cà phê" : "Thêm hạt cà phê mới"}
            </h3>
            <Button
              type="primary"
              icon={<QuestionCircleOutlined />}
              onClick={() => {
                setIsModalTourOpen(true);
              }}
              size="small"
            >
              Hướng dẫn điền form
            </Button>
          </div>

          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            onReset={handleCloseModal}
          >
            <CoffeeForm
              onImagesChange={handleImagesChange}
              onImagesPreviewChange={handleImagesPreviewChange}
              onDriveImageSelect={handleDriveImageSelect}
              selectedFiles={imageFiles}
              selectedFilesPreview={imageFilesPreview}
              driveImages={driveImages}
              driveImagesPreview={driveImagesPreview}
              editingCoffee={editingCoffee}
              onRemoveLocalImage={handleRemoveLocalImage}
              onRemoveLocalPreviewImage={handleRemoveLocalPreviewImage}
              onRemoveDriveImage={handleRemoveDriveImage}
              onRemoveDrivePreviewImage={handleRemoveDrivePreviewImage}
              setDriveImages={setDriveImages}
              setDriveImagesPreview={setDriveImagesPreview}
              newProcessingMethod={newProcessingMethod}
              setNewProcessingMethod={setNewProcessingMethod}
              uploadProgress={uploadProgress}
              nameFieldRef={nameFieldRef}
              codeFieldRef={codeFieldRef}
              roastLevelFieldRef={roastLevelFieldRef}
              beanInfoFieldRef={beanInfoFieldRef}
              regionFieldRef={regionFieldRef}
              flavorFieldRef={flavorFieldRef}
              altitudeFieldRef={altitudeFieldRef}
              processingMethodFieldRef={processingMethodFieldRef}
              brewingMethodsFieldRef={brewingMethodsFieldRef}
              beanTypeFieldRef={beanTypeFieldRef}
              roastDateFieldRef={roastDateFieldRef}
              weightPriceFieldRef={weightPriceFieldRef}
              singleOriginFieldRef={singleOriginFieldRef}
              imagesFieldRef={imagesFieldRef}
              cuppingScoreFieldRef={cuppingScoreFieldRef}
              flavorScoreFieldRef={flavorScoreFieldRef}
              regionsProp={regions}
              regionsLoadingProp={regionsLoading}
              flavorsProp={flavors}
              flavorsLoadingProp={flavorsLoading}
            />
            <Form.Item>
              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={submitLoading}
                  disabled={submitLoading}
                  className="admin-button"
                  ref={submitButtonRef}
                >
                  {editingCoffee ? 'Cập nhật' : 'Thêm mới'}
                </Button>
                <Button
                  onClick={handleCloseModal}
                  disabled={submitLoading}
                >
                  Hủy
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </div>

        {/* Modal Tour component */}
        <ConfigProvider locale={viVN}>
          <Tour
            open={isModalTourOpen}
            onClose={() => setIsModalTourOpen(false)}
            steps={modalTourSteps}
            indicatorsRender={(current, total) => (
              <span>{current + 1} / {total}</span>
            )}
            type="primary"
            arrow={true}
            placement="bottom"
            mask={{
              style: {
                boxShadow: 'inset 0 0 15px #333',
              },
            }}
          />
        </ConfigProvider>
      </Modal>

      <Modal
        title="Nhập Code"
        visible={isCodeModalVisible}
        onCancel={handleCodeModalCancel}
        footer={[
          <Button key="cancel" onClick={handleCodeModalCancel}>
            Hủy
          </Button>,
          <Button key="submit" type="primary" onClick={handleRefreshToken} loading={refreshTokenLoading} disabled={refreshTokenLoading}>
            Xác nhận
          </Button>
        ]}
      >
        <Input
          placeholder="Nhập code"
          value={zaloCode}
          onChange={(e) => setZaloCode(e.target.value)}
        />
      </Modal>
    </AdminLayout>
  );
};

export default CoffeeManagement;

